import { z } from 'zod';
import {
    EMAIL_INPUT,
    FIRST_NAME_INPUT,
    MESSAGE_INPUT,
    NAME_INPUT,
    PLACE_INPUT,
} from '@modules/validation/util/form';
import { parsePhone } from '@modules/root/util/phone';

/**
 * Builds a phone-number schema that validates against the user's market
 * (NZ/AU) using libphonenumber-js via {@link parsePhone}. Unlike the shared
 * `PHONE_NUMBER_INPUT` (which only checks character shape), this rejects
 * numbers that aren't a real, dialable number for the given country, giving
 * the user immediate feedback before they submit.
 */
const makeEnquiryPhoneInput = (country?: string) =>
    z
        .string({ required_error: 'Phone number is required' })
        .trim()
        .min(1, { message: 'Phone number is required' })
        .refine((value) => parsePhone(value, country) !== null, {
            message: 'Please enter a valid phone number',
        });

/**
 * Suburb / postcode, required. Only the forms that still collect it extend the
 * base with this; everywhere else the field is optional and simply never set.
 */
const REQUIRED_PLACE = z.object(
    {
        header: z.string(),
        value: PLACE_INPUT,
    },
    {
        required_error: 'Suburb / Postcode is required',
        invalid_type_error: 'Suburb / Postcode is required',
    },
);

const getEnquiryFormSchemaBase = (country?: string) =>
    z.object({
        name: NAME_INPUT,
        email: EMAIL_INPUT,
        phoneNumber: makeEnquiryPhoneInput(country),
        // Optional in the base so the shape stays the same for every caller —
        // the revamped forms drop the field entirely, and a key that comes and
        // goes turns the inferred type into a union nobody can read from.
        postcode: REQUIRED_PLACE.optional(),
        branchId: z.string().optional(),
        buildHubId: z.string().optional(),
        message: MESSAGE_INPUT,
    });

export interface EnquiryFormSchemaOptions {
    /** Require a full name for the name field. Defaults to `true`. */
    fullName?: boolean;
    /** Include the branch field in the form. Defaults to `false`. */
    includeBranch?: boolean;
    /**
     * The user's market country code (e.g. `NZ`, `AU`) used to validate the
     * phone number. When omitted, only international-format numbers (with a
     * `+` prefix) can be validated.
     */
    country?: string;
    /**
     * Require the suburb / postcode. Defaults to `false` — the revamped forms
     * do not ask for it, and the professional gets the project's location from
     * the attached BuildHub project instead. The legacy inline form still
     * collects it and opts in.
     */
    collectLocation?: boolean;
}

/**
 * Get the validation schema for the enquiry form.
 */
export function getEnquiryFormSchema({
    fullName = true,
    includeBranch = false,
    country,
    collectLocation = false,
}: EnquiryFormSchemaOptions = {}) {
    let formSchema = getEnquiryFormSchemaBase(country);
    if (!fullName) {
        formSchema = formSchema.extend({ name: FIRST_NAME_INPUT });
    }
    // Extended rather than reassigned: each of these narrows a field's type,
    // so the result is a different schema type from the base.
    const withLocation = collectLocation
        ? formSchema.extend({ postcode: REQUIRED_PLACE })
        : formSchema;
    if (includeBranch) {
        return withLocation.extend({
            branchId: z.string({ required_error: 'Branch is required' }),
        });
    }
    return withLocation;
}
